import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';

/**
 * Generic HLS player. Used for Turing Vision camera streams (and any
 * future cloud-camera provider that hands back an .m3u8 URL).
 *
 * Turing's `/nest/live/hls` returns a FINITE playlist that ends after the
 * bundled segments play out. To present a continuous "live" feed we:
 *   - Refresh the playlist URL roughly every 25 s (and on stall) using
 *     hls.loadSource() on the same Hls instance, so the underlying
 *     <video> element keeps playing and there is no remount jank.
 *   - Hide the native controls timeline so the per-clip currentTime reset
 *     (0:00 → 0:30 → 0:00 → ...) is not visible to the user. A small mute
 *     toggle is provided as an overlay control.
 */
export const HlsVideo: React.FC<{
  src: string;
  className?: string;
  objectFit?: React.CSSProperties['objectFit'];
  onRefreshStreamUrl?: () => Promise<string | null>;
}> = ({ src, className, objectFit = 'contain', onRefreshStreamUrl }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [status, setStatus] = useState<'loading' | 'playing' | 'retrying' | 'failed'>('loading');
  const [muted, setMuted] = useState(true);

  // Swap the playlist URL on the live Hls instance without recreating it —
  // this keeps the <video> element playing across clip boundaries.
  const swapSource = async (newUrl: string) => {
    const hls = hlsRef.current;
    if (hls) {
      hls.loadSource(newUrl);
    } else if (videoRef.current) {
      videoRef.current.src = newUrl;
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    setStatus('loading');

    let refreshAttempted = false;
    let mediaRecoveryAttempts = 0;
    let refreshInFlight = false;
    const fail = () => {
      setStatus('failed');
    };
    const onPlaying = () => {
      setStatus('playing');
    };
    video.addEventListener('playing', onPlaying);

    if (Hls.isSupported()) {
      // §28.518.85: inject JWT into HLS.js requests bound for our connector-api
      // HLS proxy so manifests + segments authenticate. Turing CDN URLs
      // fetch anonymously (they carry their own signed query params).
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        liveDurationInfinity: true,
        maxBufferLength: 30,
        backBufferLength: 15,
        manifestLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 1500,
        manifestLoadingMaxRetryTimeout: 30_000,
        levelLoadingMaxRetry: 6,
        fragLoadingMaxRetry: 6,
        xhrSetup: (xhr: XMLHttpRequest, url: string) => {
          if (url.includes('/integrations/turing/hls-proxy/')) {
            const tok = localStorage.getItem('intrlock_token');
            if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);
          }
        },
        fetchSetup: (ctx: any, initParams: any) => {
          const url = ctx.url || '';
          const headers = new Headers(initParams?.headers || {});
          if (url.includes('/integrations/turing/hls-proxy/')) {
            const tok = localStorage.getItem('intrlock_token');
            if (tok) headers.set('Authorization', `Bearer ${tok}`);
          }
          return new Request(url, { ...initParams, headers });
        },
      } as any);
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, async (_e, data) => {
        if (data.fatal) {
          console.warn('Camera HLS playback error', {
            type: data.type,
            details: data.details,
            responseCode: (data as any).response?.code,
          });
        }
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveryAttempts < 3) {
          mediaRecoveryAttempts += 1;
          setStatus('retrying');
          // A live RTSP source can begin between keyframes or contain a single
          // damaged transport packet. Let hls.js reset MediaSource and resume
          // at the next decodable keyframe instead of permanently failing the
          // tile on the first Chrome pipeline error.
          hls.recoverMediaError();
          return;
        }
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !refreshAttempted && onRefreshStreamUrl) {
          refreshAttempted = true;
          setStatus('retrying');
          try {
            const fresh = await onRefreshStreamUrl();
            if (fresh) {
              await swapSource(fresh);
              setStatus('playing');
              return;
            }
          } catch {}
          fail();
        } else {
          fail();
        }
      });
      // When the playlist runs out (clip ended) jump straight to a refresh
      // instead of waiting for the periodic timer.
      const onEnded = async () => {
        if (!onRefreshStreamUrl) return;
        try {
          const fresh = await onRefreshStreamUrl();
          if (fresh) await swapSource(fresh);
        } catch {}
      };
      video.addEventListener('ended', onEnded);
      // Some provider playlists are issued before their first segment is
      // available. hls.js keeps polling that valid manifest without emitting a
      // fatal error, which previously left the tile spinning forever. Refresh
      // the provider session when startup stalls.
      const refreshTimer = window.setInterval(async () => {
        if (!onRefreshStreamUrl || refreshInFlight) return;
        const stalled = video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA;
        if (!stalled && !video.ended && video.currentTime > 0) return;
        refreshInFlight = true;
        if (stalled) setStatus('retrying');
        try {
          const fresh = await onRefreshStreamUrl();
          if (fresh) {
            hls.stopLoad();
            hls.loadSource(fresh);
            hls.startLoad(-1);
          }
        } finally {
          refreshInFlight = false;
        }
      }, 8_000);
      return () => {
        window.clearInterval(refreshTimer);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('ended', onEnded);
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      const onLoaded = () => { void video.play().catch(() => {}); };
      const onErr = () => fail();
      const onEnded = async () => {
        if (!onRefreshStreamUrl) return;
        try {
          const fresh = await onRefreshStreamUrl();
          if (fresh) { video.src = fresh; await video.play().catch(() => {}); }
        } catch {}
      };
      video.addEventListener('loadedmetadata', onLoaded);
      video.addEventListener('error', onErr);
      video.addEventListener('ended', onEnded);
      return () => {
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onErr);
        video.removeEventListener('ended', onEnded);
      };
    }
    return () => {
      video.removeEventListener('playing', onPlaying);
    };
  }, [src, onRefreshStreamUrl]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className="relative w-full h-full bg-black">
      <video
        ref={videoRef}
        playsInline
        autoPlay
        muted={muted}
        className={className}
        style={{ width: '100%', height: '100%', objectFit }}
      />
      {/* Live badge */}
      <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/55 text-white text-[10px] font-bold px-2 py-[3px] rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        LIVE
      </div>
      {/* Mute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
        className="absolute bottom-2 right-2 bg-black/55 hover:bg-black/75 text-white text-xs font-medium px-2 py-1 rounded-full"
      >
        {muted ? '🔇' : '🔊'}
      </button>
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40 pointer-events-none">Starting stream…</div>
      )}
      {status === 'retrying' && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40 pointer-events-none">Camera is initializing… retrying</div>
      )}
      {status === 'failed' && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/60 pointer-events-none">Stream unavailable. Close and try again.</div>
      )}
    </div>
  );
};
